import type { ReactElement } from 'react';
import {
  LIVE_STATUS_SEVERITY_LABELS,
  isLiveStatusAttention,
  liveStatusTimeLabel,
  liveStatusWorkerLabel,
  shouldAnnounceLiveStatus,
  visibleLiveStatusEvents,
  type LiveStatusEvent,
} from '../lib/live-status';
import '../live-status-bar.css';

export interface LiveStatusBarProps {
  /** Newest event first. The bar never synthesizes or scrapes events. */
  readonly events: readonly LiveStatusEvent[];
  /** Total rows including the prominent latest event. Defaults to four. */
  readonly maxVisible?: number;
  readonly onWorkerSelect?: (workerId: string) => void;
  readonly attentionCount?: number;
  readonly compact?: boolean;
  /** Native disclosure so the owner can reclaim vertical space without losing live status. */
  readonly collapsible?: boolean;
  readonly defaultCollapsed?: boolean;
  readonly ariaLabel?: string;
}

function eventLabel(event: LiveStatusEvent): string {
  const parts = [
    LIVE_STATUS_SEVERITY_LABELS[event.severity],
    event.worker ? liveStatusWorkerLabel(event.worker) : null,
    event.source?.trim() || null,
    event.message.trim(),
  ];
  return parts.filter((part): part is string => part !== null && part !== '').join('. ');
}

function renderEventEntry(
  event: LiveStatusEvent,
  latest: boolean,
  onWorkerSelect?: (workerId: string) => void,
): ReactElement {
  const worker = event.worker ?? null;
  const workerLabel = worker === null ? null : liveStatusWorkerLabel(worker);
  const source = event.source?.trim() || null;
  const className = [
    'live-status-entry',
    `live-status-severity-${event.severity}`,
    latest ? 'live-status-latest live-status-transient' : '',
    isLiveStatusAttention(event.severity) ? 'live-status-attention' : '',
  ].filter(Boolean).join(' ');

  const content = (
    <>
      <span className="live-status-severity-label">{LIVE_STATUS_SEVERITY_LABELS[event.severity]}</span>
      <span className="live-status-message">{event.message}</span>
      <span className="live-status-meta">
        {workerLabel !== null && <span>{workerLabel}</span>}
        {source !== null && <span>{source}</span>}
        <time dateTime={event.at}>{liveStatusTimeLabel(event.at)}</time>
      </span>
    </>
  );

  if (worker !== null && onWorkerSelect !== undefined) {
    return (
      <button
        type="button"
        className={className}
        data-motion={latest ? 'transient' : 'static'}
        aria-label={`Select ${workerLabel}: ${event.message}`}
        onClick={() => onWorkerSelect(worker.id)}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={className}
      data-motion={latest ? 'transient' : 'static'}
      aria-label={eventLabel(event)}
    >
      {content}
    </div>
  );
}

export function LiveStatusBar({
  events,
  maxVisible = 4,
  onWorkerSelect,
  attentionCount = 0,
  compact = false,
  collapsible = false,
  defaultCollapsed = false,
  ariaLabel = 'Brunel live status',
}: LiveStatusBarProps): ReactElement {
  const visible = visibleLiveStatusEvents(events, maxVisible);
  const latest = visible[0] ?? null;
  const recent = compact ? [] : visible.slice(1);
  const attention = Number.isFinite(attentionCount) ? Math.max(0, Math.trunc(attentionCount)) : 0;
  const className = `live-status-bar${compact ? ' live-status-compact' : ''}`;

  const heading = (
    <div className="live-status-heading">
      <span className="live-status-title">Live</span>
      {attention > 0 && (
        <span className="live-status-count" aria-label={`${attention} items need attention`}>
          {attention} attention
        </span>
      )}
    </div>
  );

  const body = latest === null ? (
    <div className="live-status-empty">No recent activity</div>
  ) : (
    <>
      {renderEventEntry(latest, true, onWorkerSelect)}

      {recent.length > 0 && (
        <ol className="live-status-history" aria-label="Recent status events">
          {recent.map((event) => (
            <li key={event.id}>
              {renderEventEntry(event, false, onWorkerSelect)}
            </li>
          ))}
        </ol>
      )}

      {shouldAnnounceLiveStatus(latest.severity) && (
        <span className="live-status-announcer" aria-live="polite" aria-atomic="true">
          {eventLabel(latest)}
        </span>
      )}
    </>
  );

  if (collapsible) {
    return (
      <details className={`${className} live-status-collapsible`} aria-label={ariaLabel} open={!defaultCollapsed}>
        <summary className="live-status-summary">
          {heading}
          <span className="live-status-toggle-hint" aria-hidden="true">⌄</span>
        </summary>
        <div className="live-status-body">{body}</div>
      </details>
    );
  }

  return (
    <section className={className} aria-label={ariaLabel}>
      {latest !== null && heading}
      {body}
    </section>
  );
}
